import React, { useState } from "react";
import axios from "axios";
import { ThumbsUp, ChevronDown, Star, Loader2, CheckCircle2, AlertCircle, PenSquare, X, ImagePlus } from "lucide-react";
import { restaurant, reviews as mockReviews } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PHOTOS = 4;
const MAX_DIM = 1200;
const JPEG_QUALITY = 0.75;

const Bar = ({ value }) => (
  <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
    <div className="h-full bg-[#1B7B71] rounded-full" style={{ width: `${(value / 10) * 100}%` }} />
  </div>
);

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays < 1) return "Aujourd'hui";
  if (diffDays === 1) return "Hier";
  if (diffDays < 30) return `Il y a ${diffDays} jours`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
};

// Resize + compress an image file client-side to a data URL
const resizeImageFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Impossible de lire le fichier"));
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) {
            height = Math.round((height * MAX_DIM) / width);
            width = MAX_DIM;
          } else {
            width = Math.round((width * MAX_DIM) / height);
            height = MAX_DIM;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
      };
      img.onerror = () => reject(new Error("Format d'image non supporté"));
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const RatingPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((n) => {
      const filled = value >= n * 2;
      const half = value >= n * 2 - 1 && value < n * 2;
      return (
        <button type="button" key={n} onClick={() => onChange(n * 2)} className="p-0.5" aria-label={`${n * 2}/10`}>
          <Star className={`h-6 w-6 transition-colors ${filled ? "fill-[#F6B93B] text-[#F6B93B]" : half ? "fill-[#F6B93B]/50 text-[#F6B93B]" : "text-gray-300"}`} />
        </button>
      );
    })}
    <span className="ml-2 text-sm font-semibold text-gray-700">{value}/10</span>
  </div>
);

const ReviewForm = ({ onSubmitted, onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(10);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]); // data URLs
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onSelectFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, MAX_PHOTOS - photos.length);
    if (files.length === 0) return;
    setProcessing(true);
    setError(null);
    try {
      const compressed = await Promise.all(files.map((f) => resizeImageFile(f)));
      setPhotos((p) => [...p, ...compressed].slice(0, MAX_PHOTOS));
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
      e.target.value = ""; // allow re-selecting same file
    }
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/reviews`, {
        name,
        email: email || null,
        rating,
        text,
        photos,
      });
      onSubmitted(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-gradient-to-br from-[#F6FAF9] to-white border border-[#1B7B71]/20 rounded-2xl p-5 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <PenSquare className="h-5 w-5 text-[#1B7B71]" />
          Partagez votre expérience
        </h3>
        <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700" aria-label="Fermer">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Votre note</label>
          <RatingPicker value={rating} onChange={setRating} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            required
            type="text"
            placeholder="Votre nom (ex. Marie D.)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7B71]"
          />
          <input
            type="email"
            placeholder="Votre e-mail (optionnel)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7B71]"
          />
        </div>

        <textarea
          required
          rows={4}
          placeholder="Racontez votre expérience : accueil, plats préférés, ambiance…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7B71] resize-none"
        />

        {/* Photo upload */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-2">
            Ajouter vos photos ({photos.length}/{MAX_PHOTOS}) — optionnel
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            {photos.map((src, i) => (
              <div key={i} className="relative h-20 w-20 rounded-lg overflow-hidden border border-gray-200">
                <img src={src} alt={`upload ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5"
                  aria-label="Retirer"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <label className={`h-20 w-20 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 cursor-pointer hover:border-[#1B7B71] hover:text-[#1B7B71] transition-colors text-xs ${processing ? "opacity-50 pointer-events-none" : ""}`}>
                {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                <span>{processing ? "…" : "Ajouter"}</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onSelectFiles}
                  className="hidden"
                  disabled={processing}
                />
              </label>
            )}
          </div>
          <p className="text-[11px] text-gray-500 mt-1.5">Jusqu'à {MAX_PHOTOS} photos. Elles seront ajoutées à la galerie du restaurant.</p>
        </div>

        {error && (
          <div className="flex items-start gap-2 text-sm bg-red-50 border border-red-200 text-red-700 rounded-lg p-3">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={loading || processing}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-[#1B7B71] text-white font-semibold rounded-lg hover:bg-[#155f57] transition-colors disabled:opacity-60"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Publier mon avis
        </button>
      </div>
    </form>
  );
};

const Reviews = ({ customReviews, onNewReview, onOpenPhoto }) => {
  const [visible, setVisible] = useState(6);
  const [helpful, setHelpful] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);

  const allReviews = [
    ...customReviews.map((r) => ({
      name: r.name,
      date: formatDate(r.created_at),
      rating: r.rating,
      text: r.text,
      photos: r.photos || [],
      isNew: true,
    })),
    ...mockReviews,
  ];

  const toggleHelpful = (i) => setHelpful((s) => ({ ...s, [i]: !s[i] }));

  const onSubmitted = (r) => {
    onNewReview(r);
    setShowForm(false);
    setSuccessMsg(
      r.photos && r.photos.length > 0
        ? `Merci ! Votre avis et vos ${r.photos.length} photo${r.photos.length > 1 ? "s" : ""} ont été publiés.`
        : "Merci ! Votre avis a bien été publié."
    );
    setTimeout(() => setSuccessMsg(null), 5000);
  };

  return (
    <section id="avis" className="py-8 border-b border-gray-100">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Avis</h2>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#1B7B71] text-white text-sm font-semibold rounded-full hover:bg-[#155f57] transition-colors shadow-sm"
          >
            <PenSquare className="h-4 w-4" /> Laisser un avis
          </button>
        )}
      </div>

      {successMsg && (
        <div className="flex items-center gap-2 text-sm bg-[#E6F3F1] border border-[#1B7B71]/30 text-[#155f57] rounded-lg p-3 mb-4">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {showForm && <ReviewForm onSubmitted={onSubmitted} onClose={() => setShowForm(false)} />}

      <div className="flex flex-col md:flex-row gap-8 mb-6">
        <div className="flex items-center gap-4">
          <div className="bg-[#1B7B71] text-white rounded-xl w-20 h-20 flex flex-col items-center justify-center">
            <div className="text-2xl font-bold leading-none">{restaurant.rating}</div>
            <div className="text-[10px] uppercase tracking-wide mt-1">/ 10</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">Excellent</div>
            <div className="text-sm text-gray-600">
              {(restaurant.reviewsCount + customReviews.length).toLocaleString("fr-FR")} avis
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-2 max-w-md">
          {Object.entries(restaurant.scores).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3">
              <span className="text-sm text-gray-700 w-20 capitalize">{k}</span>
              <Bar value={v} />
              <span className="text-sm font-semibold text-gray-900 w-8 text-right">{v}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#F6FAF9] border border-gray-200 rounded-xl p-5 mb-6">
        <div className="font-semibold text-gray-900 mb-2">Points forts d'après les avis des clients</div>
        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4">
          {restaurant.highlights.map((h, i) => <li key={i}>{h}</li>)}
        </ul>
        <div className="font-semibold text-gray-900 mb-2">Ce qu'en disent nos clients</div>
        <p className="text-sm text-gray-700 leading-relaxed">{restaurant.aiSummary}</p>
      </div>

      <h3 className="text-lg font-semibold text-gray-900 mb-4">Tous les avis</h3>
      <div className="space-y-5">
        {allReviews.slice(0, visible).map((r, i) => (
          <div key={i} className="flex gap-4 pb-5 border-b border-gray-100 last:border-0">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1B7B71] to-[#2a9d8f] text-white flex items-center justify-center font-semibold flex-shrink-0">
              {r.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-semibold text-gray-900 flex items-center gap-2">
                    {r.name}
                    {r.isNew && (
                      <span className="text-[10px] font-bold uppercase tracking-wide bg-[#E6F3F1] text-[#1B7B71] px-1.5 py-0.5 rounded">Nouveau</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">{r.date}</div>
                </div>
                <div className="bg-[#E6F3F1] text-[#1B7B71] text-sm font-bold px-2.5 py-1 rounded-md">
                  {r.rating.toString().replace(".", ",")}
                </div>
              </div>
              <p className="text-sm text-gray-700 mt-2 leading-relaxed whitespace-pre-line">{r.text}</p>

              {r.photos && r.photos.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {r.photos.map((src, pi) => (
                    <button
                      key={pi}
                      onClick={() => onOpenPhoto && onOpenPhoto(src)}
                      className="h-20 w-20 rounded-lg overflow-hidden border border-gray-200 hover:opacity-90 transition-opacity"
                    >
                      <img src={src} alt={`Avis ${r.name} ${pi + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              <button
                onClick={() => toggleHelpful(i)}
                className={`mt-3 flex items-center gap-1.5 text-xs font-medium transition-colors ${
                  helpful[i] ? "text-[#1B7B71]" : "text-gray-500 hover:text-[#1B7B71]"
                }`}
              >
                <ThumbsUp className="h-3.5 w-3.5" />
                utile {helpful[i] ? "· merci !" : ""}
              </button>
            </div>
          </div>
        ))}
      </div>

      {visible < allReviews.length && (
        <button
          onClick={() => setVisible((v) => v + 6)}
          className="mt-6 w-full flex items-center justify-center gap-1 py-3 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Voir plus d'avis ({allReviews.length - visible}) <ChevronDown className="h-4 w-4" />
        </button>
      )}
    </section>
  );
};

export default Reviews;
